import type { AutoResumeLatchReason } from '@core/realtime/autoResumeLatch';
import type { RelayConnState } from './rithmicRelay';

/**
 * Pure Rithmic UI-state derivations, mirrored from `cqgUiState.ts` so the badge + dialog stay a single
 * source of truth (no React/socket mocking needed to pin the "badge live over a dead socket" /
 * "login form shown while connected" regression class).
 */

/** BE session state as serialised by /api/v1/rithmic/status + the `rithmic.state` push (camelCase). */
export type RithmicBeState = 'disconnected' | 'connecting' | 'live' | 'error';

const RITHMIC_STATES: readonly RithmicBeState[] = ['disconnected', 'connecting', 'live', 'error'];

/**
 * Normalise a raw BE/push state string to a KNOWN `RithmicBeState`, defaulting to 'disconnected' for
 * anything empty/unrecognised. The single normaliser shared by the badge hook AND the dialog so a
 * malformed push (`''`, a typo, a new BE state we don't model) can never write a junk value into the
 * shared `['rithmic','status']` cache — a class of bug the CQG side already learned.
 */
export function normaliseRithmicState(state: string | undefined | null): RithmicBeState {
  const key = (state ?? 'disconnected').toLowerCase() as RithmicBeState;
  return RITHMIC_STATES.includes(key) ? key : 'disconnected';
}

/**
 * What BOTH the topbar badge AND the connect dialog display — the SINGLE source of truth for the
 * Rithmic connection status. A faithful, real-time read combining the BE session state with THIS
 * tab's browser→Rithmic socket:
 *   - **error** wins — a rejected/forced logon shows the red error badge, never live;
 *   - **live** only when the BE session is live AND this tab's socket is actually OPEN (data can flow);
 *   - **connecting** while logging on / the socket is opening — INCLUDING a live session whose socket
 *     is closed/opening (e.g. just after a refresh, before auto-resume re-opens the pipe);
 *   - **disconnected** otherwise.
 *
 * An open socket alone is NOT "live" (Rithmic accepts the WebSocket before auth — a rejected logon
 * leaves it open), and a live session alone is NOT "live" if this tab's socket is down. Both must hold.
 */
export function deriveBadgeDisplay(beState: RithmicBeState, socket: RelayConnState, passive = false): RithmicBeState {
  if (beState === 'error') return 'error';
  // Another tab owns the socket (D1, 2026-09-13): the BE session is live THERE — report it as connected,
  // not as this tab's dead pipe (which would read "connecting" forever and invite a resume war).
  if (beState === 'live' && passive) return 'live';
  if (beState === 'live') return socket === 'open' ? 'live' : 'connecting';
  if (beState === 'connecting' || socket === 'opening') return 'connecting';
  return 'disconnected';
}

/**
 * Whether a lost/rejected Rithmic session may be auto-resumed with the stored credentials.
 *
 * Mirrors the CQG reconnect-storm fix: a Reject / ForcedLogout surfaces as `beState === 'error'`.
 * Re-sending the SAME rejected credentials on every SignalR blip would hammer Rithmic (and risks
 * locking the account), so a terminal rejection ('error') is NOT auto-resumable — the user must
 * explicitly Connect again; a transient loss ('disconnected') still is.
 *
 * `latch` is the durable latch (`readAutoResumeLatch('rithmic')`, 2026-09-13 D4): a recorded kick,
 * rejection or user Disconnect refuses auto-resume whatever the BE state says — the BE may have
 * dropped the session (`disconnected`) by the time a reloaded tab asks, and that is exactly the
 * reload → re-login loop this guards against.
 */
export function shouldAutoResume(beState: RithmicBeState, latch: AutoResumeLatchReason | null = null): boolean {
  if (latch) return false;
  return beState !== 'error';
}
